<template>
  <section
    id="plan-horaires"
    class="plan-h"
  >
    <div class="container">
      <SectionHead
        :icon="data.planHoraires.icon"
        :title="data.planHoraires.title"
        light
      />

      <div class="plan-h__map-panel">
        <div class="plan-h__map-wrap">
          <img
            class="plan-h__map"
            :src="data.planHoraires.plan"
            alt="Plan du Centre Sud"
          >
          <img
            v-if="activeHighlight"
            class="plan-h__hl"
            :src="activeHighlight"
            alt=""
          >
          <svg
            v-if="hitShapes.length"
            class="plan-h__hit"
            viewBox="0 0 1536 1024"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              v-for="h in hitShapes"
              :key="'h' + h.plan"
              :d="h.d"
              @pointerdown="notePointer"
              @pointerenter="enter(h.plan, $event)"
              @pointerleave="leave($event)"
              @click="pick(h.plan)"
            />
          </svg>

          <span
            v-for="b in badges"
            :key="'b' + b.n"
            class="plan-h__badge"
            :style="{ left: b.x + '%', top: b.y + '%' }"
            aria-hidden="true"
          >{{ b.n }}</span>
          <div
            v-if="carrefour"
            class="plan-h__carrefour"
            :class="{ 'is-hl': hoverPlan === carrefour.plan }"
            :style="{ left: carrefour.x + '%', top: carrefour.y + '%' }"
            aria-hidden="true"
            @pointerdown="notePointer"
            @pointerenter="enter(carrefour.plan, $event)"
            @pointerleave="leave($event)"
            @click="pick(carrefour.plan)"
          >
            <img
              :src="carrefour.logo"
              alt=""
            >
            <span class="plan-h__carrefour-num">{{ carrefour.plan }}</span>
          </div>
        </div>
      </div>

      <div class="plan-h__cols">
        <div class="plan-h__shops">
          <h3 class="plan-h__subtitle">{{ data.planHoraires.shopsTitle }}</h3>
          <ul class="plan-h__shop-list">
            <!-- boutons : iOS Safari n'émet aucun évènement souris sur un simple <li>,
                 le survol de la liste y était donc inopérant -->
            <li
              v-for="shop in sortedShops"
              :key="shop.name"
            >
              <button
                type="button"
                class="plan-h__shop"
                :class="{ 'is-hl': hoverPlan === shop.plan }"
                :aria-pressed="hoverPlan === shop.plan ? 'true' : 'false'"
                @pointerdown="notePointer"
                @pointerenter="enter(shop.plan, $event)"
                @pointerleave="leave($event)"
                @click="pick(shop.plan)"
              >
                <span class="plan-h__shop-num">{{ shop.plan }}</span>
                <span class="plan-h__shop-name">{{ shop.name }}</span>
              </button>
            </li>
          </ul>
        </div>

        <span class="plan-h__divider" />

        <div class="plan-h__hours">
          <h3 class="plan-h__subtitle">{{ data.planHoraires.hoursTitle }}</h3>
          <table class="plan-h__hours-table">
            <tbody>
              <tr
                v-for="row in data.planHoraires.hours"
                :key="row.day"
              >
                <td>{{ row.day }}</td>
                <td>{{ row.time }}</td>
              </tr>
            </tbody>
          </table>

          <h3 class="plan-h__subtitle plan-h__subtitle--resto">{{ data.planHoraires.restaurantsTitle }}</h3>
          <p class="plan-h__resto-note">{{ data.planHoraires.restaurantsNote }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import SectionHead from '@/components/SectionHead.vue'

export default {
  name: 'PlanHorairesSection',
  components: { SectionHead },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      hoverPlan: null,
      shapes: null,
      // type du dernier appui (souris / doigt / stylet), lu au clic qui suit
      lastPointer: 'mouse'
    }
  },
  watch: {
    // l'URL change quand le contenu Strapi remplace le contenu local
    'data.planHoraires.shapes': {
      immediate: true,
      handler(url) {
        if (!url) return
        fetch(url)
          .then((r) => r.json())
          .then((json) => { this.shapes = json })
          .catch((err) => {
            // sans contours, le survol depuis la liste continue de fonctionner
            console.warn('[centre-sud] contours du plan indisponibles', err)
          })
      }
    }
  },
  methods: {
    // Souris : le survol allume la cellule, la sortie l'éteint.
    // Tactile : pas de survol ; chaque appui allume la cellule, un 2e appui sur la
    // même l'éteint. (Avant, l'appui déclenchait « entrée » puis « clic » : la
    // cellule s'allumait et s'éteignait aussitôt.)
    notePointer(e) {
      this.lastPointer = e.pointerType || 'mouse'
    },
    enter(plan, e) {
      if (e.pointerType === 'mouse') this.hoverPlan = plan
    },
    leave(e) {
      if (e.pointerType === 'mouse') this.hoverPlan = null
    },
    pick(plan) {
      if (this.lastPointer === 'mouse') {
        this.hoverPlan = plan
        return
      }
      this.hoverPlan = this.hoverPlan === plan ? null : plan
    }
  },
  computed: {
    sortedShops() {
      return [...this.data.boutiques.shops].sort((a, b) =>
        a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })
      )
    },
    // surbrillance jaune de la cellule survolée : une seule image, chargée à la
    // demande (les 17 visuels d'un coup alourdiraient inutilement la page)
    activeHighlight() {
      if (this.hoverPlan == null) return null
      const shop = this.data.boutiques.shops.find(
        (s) => s.plan === this.hoverPlan && s.highlight
      )
      return shop ? shop.highlight : null
    },
    badges() {
      return this.data.planHoraires.badges || []
    },
    // zones survolables sur le plan : uniquement les cellules occupées par une enseigne
    hitShapes() {
      if (!this.shapes) return []
      return this.data.boutiques.shops
        .map((s) => ({ plan: s.plan, d: this.shapes[String(s.plan)] }))
        .filter((h) => h.d)
    },
    carrefour() {
      return this.data.planHoraires.carrefour || null
    }
  }
}
</script>

<style lang="scss" scoped>
// section marine, motif en filigrane comme les actus
.plan-h {
  background: $navy;
  padding: 36px 0 44px;
  position: relative;
  overflow: hidden;

  &::before {
    @include curves-overlay($curves-dark);
  }

  .container {
    position: relative;
  }
}

// panneau sarcelle arrondi avec le plan blanc
.plan-h__map-panel {
  margin-top: 20px;
  background: $teal;
  border-radius: 18px;
  padding: clamp(12px, 1.4vw, 18px);
}

// Le plan occupe la place qui reste sous le titre une fois la légende « Boutiques
// du plan » réservée, pour que toute la section tienne sur un écran de portable.
// 460px = hauteur de tout ce qui n'est pas le plan (titre, marges, légende) ;
// ×1.5 = proportion de l'image (1536×1024). Plus l'écran est haut, plus le plan
// est grand, sans jamais repousser la légende hors de l'écran.
.plan-h__map-wrap {
  position: relative;
  container-type: inline-size;
  width: min(100%, 780px, max(400px, calc((100vh - 460px) * 1.5)));
  margin: 0 auto;
}

.plan-h__map {
  width: 100%;
  height: auto;
  display: block;
}

.plan-h__hl {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

// calque de survol : formes transparentes calées sur les cellules du plan
.plan-h__hit {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  path {
    fill: transparent;
    cursor: pointer;
  }
}

.plan-h__badge {
  position: absolute;
  transform: translate(-50%, -50%);
  background: $white;
  color: $ink;
  font-weight: 700;
  font-size: clamp(7px, 1.15cqw, 13px);
  line-height: 1;
  padding: 0.45em 0.5em;
  border-radius: 0.4em;
  box-shadow: 0 1px 3px rgba($navy, 0.35);
  pointer-events: none;
}

.plan-h__carrefour {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3em;
  font-size: clamp(7px, 1.3cqw, 15px);
  cursor: pointer;

  img {
    width: 5.2em;
    height: auto;
    // logo livré en couleur -> version blanche comme sur la maquette
    filter: brightness(0) invert(1);
    transition: filter 0.2s ease;
  }

  &-num {
    color: $ink;
    font-weight: 700;
    font-size: 0.85em;
  }

  &.is-hl {
    img {
      // blanc -> jaune surbrillance (#F6D74B)
      filter: brightness(0) saturate(100%) invert(87%) sepia(30%) saturate(1100%) hue-rotate(357deg) brightness(103%) contrast(94%);
    }

    .plan-h__carrefour-num {
      color: #F6D74B;
    }
  }
}

.plan-h__cols {
  display: grid;
  grid-template-columns: 1.35fr auto 1fr;
  gap: clamp(24px, 4vw, 56px);
  margin-top: 22px;
  color: $white;
}

.plan-h__subtitle {
  font-size: 19px;
  font-weight: 700;
  color: $white;

  &--resto {
    margin-top: 22px;
  }
}

.plan-h__shop-list {
  list-style: none;
  margin-top: 16px;
  columns: 2;
  column-gap: clamp(24px, 3vw, 48px);

  li {
    break-inside: avoid;
  }
}

.plan-h__shop {
  display: flex;
  align-items: baseline;
  gap: 10px;
  width: 100%;
  padding: 3px 0;
  border: none;
  background: none;
  font-family: inherit;
  color: inherit;
  text-align: left;
  -webkit-tap-highlight-color: transparent;

  &.is-hl {
    .plan-h__shop-num,
    .plan-h__shop-name {
      color: #F6D74B;
    }
  }
}

.plan-h__shop-num {
  flex: 0 0 26px;
  font-weight: 700;
  font-size: 14px;
  color: $green;
  text-align: right;
}

.plan-h__shop-name {
  font-size: 12.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.plan-h__divider {
  width: 2px;
  background: rgba($white, 0.25);
  border-radius: 2px;
}

.plan-h__hours-table {
  margin-top: 16px;
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;

  td {
    padding: 3.5px 0;

    &:first-child {
      font-weight: 600;
    }

    &:last-child {
      text-align: right;
      color: rgba($white, 0.9);
    }
  }
}

.plan-h__resto-note {
  margin-top: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: $green;
}

@include tablet-down {
  .plan-h__cols {
    grid-template-columns: 1fr;
  }

  .plan-h__divider {
    display: none;
  }
}

@include mobile {
  .plan-h {
    padding: 48px 0 60px;
  }

  .plan-h__cols {
    margin-top: 40px;
  }

  // maquette mobile : liste sur 2 colonnes, horaires et restaurants côte à côte
  .plan-h__shop-list {
    columns: 2;
    column-gap: 14px;
  }

  .plan-h__shop {
    gap: 7px;
    padding: 5px 0;
  }

  .plan-h__shop-num {
    flex-basis: 20px;
    font-size: 13px;
  }

  .plan-h__shop-name {
    font-size: 12px;
    letter-spacing: 0.02em;
  }

  .plan-h__subtitle {
    font-size: 17px;
  }

  .plan-h__hours {
    display: grid;
    grid-template-columns: 1.3fr 1fr;
    grid-template-rows: auto 1fr;
    column-gap: 18px;
    align-items: start;
  }

  .plan-h__hours-table {
    grid-column: 1;
    grid-row: 2;
    font-size: 13px;

    td:last-child {
      text-align: left;
      padding-left: 10px;
    }
  }

  .plan-h__subtitle--resto {
    grid-column: 2;
    grid-row: 1;
    margin-top: 0;
  }

  .plan-h__resto-note {
    grid-column: 2;
    grid-row: 2;
    margin-top: 16px;
  }
}
</style>
